import React from 'react';
import { Crown } from 'lucide-react';
import StatusPill from './StatusPill';
import ProgressBar from './ProgressBar';

interface Leader {
  id: string;
  name: string;
}

interface CrewCardData {
  id: string;
  name: string;
  memberCount: number;
  progressPercent?: number;
  leaderId?: string;
  leader?: Leader;
  isLeader?: boolean;
  isJoined?: boolean;
  hasPending?: boolean;
  joinBadgeCount?: number;
}

interface Props {
  data: CrewCardData;
  onClick?: () => void;
  onJoin?: () => void;
  onLeave?: () => void;
  onOpenRequests?: () => void;
}

function getInitial(s: string): string {
  return s?.trim()?.charAt(0) || '?';
}

export const CrewCard: React.FC<Props> = ({ data, onClick, onJoin, onLeave, onOpenRequests }) => {
  const status: 'joined' | 'pending' | 'not_joined' = data.isJoined ? 'joined' : (data.hasPending ? 'pending' : 'not_joined');

  return (
    <div
      className={`p-4 rounded-xl border border-slate-200 transition cursor-pointer shadow-sm ${
        data.isJoined ? 'bg-rose-50/70 hover:bg-rose-50' : 'bg-white hover:bg-slate-50'
      }`}
      onClick={onClick}
    >
      <div className="flex gap-3">
        {/* 왼쪽: 아이콘 (2줄 병합) */}
        <div className="relative w-14 h-14 rounded-lg bg-slate-700 text-white flex items-center justify-center font-bold text-xl shrink-0">
          {getInitial(data.name)}
        </div>

        {/* 오른쪽: 2줄 콘텐츠 */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          {/* 첫 번째 줄: 제목 + 왕관 + 버튼 */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <h4 className="font-bold text-slate-900 text-lg truncate">{data.name}</h4>
              {data.isLeader && (
                <Crown className="w-4 h-4 text-yellow-500 shrink-0" />
              )}
            </div>
            
            {/* 버튼 */}
            <div className="shrink-0">
              {data.isLeader ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenRequests && onOpenRequests(); }}
                  className="relative px-2.5 py-1.5 text-xs rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-700 font-semibold whitespace-nowrap"
                >
                  가입 신청
                  {data.joinBadgeCount && data.joinBadgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                      {data.joinBadgeCount}
                    </span>
                  )}
                </button>
              ) : data.isJoined ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onLeave && onLeave(); }}
                  className="px-2.5 py-1.5 text-xs rounded-lg bg-red-100 hover:bg-red-200 text-red-700 font-semibold whitespace-nowrap"
                >
                  탈퇴
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onJoin && onJoin(); }}
                  className="px-2.5 py-1.5 text-xs rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-semibold whitespace-nowrap"
                >
                  가입
                </button>
              )}
            </div>
          </div>

          {/* 두 번째 줄: 상태 배지 + 멤버 수 + 진행률 바 + 퍼센트 */}
          <div className="flex items-center gap-2">
            <StatusPill status={status}>
              {data.isJoined ? '참여중' : data.hasPending ? '참여신청' : '미참여'}
            </StatusPill>
            <span className="text-xs text-slate-600 whitespace-nowrap shrink-0">({data.memberCount}명)</span>
            <div className="flex-1 min-w-0">
              <ProgressBar percent={data.progressPercent ?? 0} />
            </div>
            <span className="text-xs font-semibold text-rose-600 whitespace-nowrap shrink-0">
              {Math.max(0, Math.min(100, Math.round(data.progressPercent ?? 0)))}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrewCard;


